import { useEffect, useRef, memo, useState } from 'react';

// Available indicators for TradingView
export const AVAILABLE_INDICATORS = [
  { id: 'RSI@tv-basicstudies', name: 'RSI', category: 'momentum', description: 'Relative Strength Index' },
  { id: 'MACD@tv-basicstudies', name: 'MACD', category: 'momentum', description: 'Moving Average Convergence Divergence' },
  { id: 'BB@tv-basicstudies', name: 'Bollinger Bands', category: 'volatility', description: 'Volatility bands' },
  { id: 'MASimple@tv-basicstudies', name: 'SMA', category: 'trend', description: 'Simple Moving Average' },
  { id: 'MAExp@tv-basicstudies', name: 'EMA', category: 'trend', description: 'Exponential Moving Average' },
  { id: 'VWAP@tv-basicstudies', name: 'VWAP', category: 'volume', description: 'Volume Weighted Average Price' },
  { id: 'Volume@tv-basicstudies', name: 'Volume', category: 'volume', description: 'Trading volume' },
  { id: 'StochasticRSI@tv-basicstudies', name: 'Stochastic RSI', category: 'momentum', description: 'Stochastic RSI oscillator' },
  { id: 'ATR@tv-basicstudies', name: 'ATR', category: 'volatility', description: 'Average True Range' },
  { id: 'ADX@tv-basicstudies', name: 'ADX', category: 'trend', description: 'Average Directional Index' },
  { id: 'CCI@tv-basicstudies', name: 'CCI', category: 'momentum', description: 'Commodity Channel Index' },
  { id: 'OBV@tv-basicstudies', name: 'OBV', category: 'volume', description: 'On Balance Volume' },
  { id: 'IchimokuCloud@tv-basicstudies', name: 'Ichimoku Cloud', category: 'trend', description: 'Ichimoku Kinko Hyo' },
  { id: 'PSAR@tv-basicstudies', name: 'Parabolic SAR', category: 'trend', description: 'Parabolic Stop and Reverse' },
  { id: 'WilliamsR@tv-basicstudies', name: 'Williams %R', category: 'momentum', description: 'Williams Percent Range' },
];

// Default indicator sets
export const INDICATOR_PRESETS = {
  default: ['Volume@tv-basicstudies'],
  momentum: ['RSI@tv-basicstudies', 'MACD@tv-basicstudies', 'Volume@tv-basicstudies'],
  trend: ['MASimple@tv-basicstudies', 'MAExp@tv-basicstudies', 'ADX@tv-basicstudies', 'Volume@tv-basicstudies'],
  volatility: ['BB@tv-basicstudies', 'ATR@tv-basicstudies', 'Volume@tv-basicstudies'],
  scalping: ['RSI@tv-basicstudies', 'VWAP@tv-basicstudies', 'Volume@tv-basicstudies'],
  swing: ['MACD@tv-basicstudies', 'BB@tv-basicstudies', 'ADX@tv-basicstudies', 'Volume@tv-basicstudies'],
};

// TradingView Advanced Chart Widget Component with customizable indicators
function TradingViewWidget({ 
  symbol = 'BINANCE:BTCUSDT', 
  theme = 'dark', 
  height = 500,
  indicators = ['Volume@tv-basicstudies'],
  interval = '60',
  onSymbolChange
}) {
  const containerRef = useRef(null);
  const widgetRef = useRef(null);

  useEffect(() => {
    // Store ref value for cleanup
    const container = containerRef.current;
    if (!container) return;

    // Clear previous widget
    container.innerHTML = '';

    // Create widget container
    const widgetContainer = document.createElement('div');
    widgetContainer.className = 'tradingview-widget-container';
    widgetContainer.style.height = '100%';
    widgetContainer.style.width = '100%';

    const widgetDiv = document.createElement('div');
    widgetDiv.className = 'tradingview-widget-container__widget';
    widgetDiv.style.height = '100%';
    widgetDiv.style.width = '100%';
    widgetContainer.appendChild(widgetDiv);

    container.appendChild(widgetContainer);

    // Create and load TradingView script
    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    script.type = 'text/javascript';
    script.async = true;
    script.innerHTML = JSON.stringify({
      autosize: true,
      symbol: symbol,
      interval: interval,
      timezone: 'Etc/UTC',
      theme: theme,
      style: '1',
      locale: 'en',
      enable_publishing: false,
      allow_symbol_change: true,
      calendar: false,
      hide_top_toolbar: false,
      hide_legend: false,
      save_image: true,
      support_host: 'https://www.tradingview.com',
      backgroundColor: theme === 'dark' ? 'rgba(10, 10, 15, 1)' : 'rgba(255, 255, 255, 1)',
      gridColor: theme === 'dark' ? 'rgba(30, 30, 45, 0.5)' : 'rgba(200, 200, 200, 0.5)',
      hide_side_toolbar: false,
      withdateranges: true,
      details: true,
      hotlist: false,
      show_popup_button: true,
      popup_width: '1000',
      popup_height: '650',
      studies: indicators,
      studies_overrides: {
        "volume.volume.color.0": "#ef5350",
        "volume.volume.color.1": "#26a69a",
      },
      overrides: {
        "mainSeriesProperties.candleStyle.upColor": "#26a69a",
        "mainSeriesProperties.candleStyle.downColor": "#ef5350",
        "mainSeriesProperties.candleStyle.borderUpColor": "#26a69a",
        "mainSeriesProperties.candleStyle.borderDownColor": "#ef5350",
        "mainSeriesProperties.candleStyle.wickUpColor": "#26a69a",
        "mainSeriesProperties.candleStyle.wickDownColor": "#ef5350",
      }
    });

    widgetContainer.appendChild(script);
    widgetRef.current = widgetContainer;

    return () => {
      // Cleanup using stored container reference
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [symbol, theme, indicators, interval]);

  return (
    <div 
      ref={containerRef} 
      style={{ height: `${height}px`, width: '100%', minHeight: '400px' }}
      className="rounded-lg overflow-hidden bg-[#0a0a0f]"
      data-testid="tradingview-chart"
    />
  );
}

// Mini Chart Widget for smaller displays
export function TradingViewMiniWidget({ symbol = 'BINANCE:BTCUSDT', theme = 'dark', height = 220 }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }

    const widgetContainer = document.createElement('div');
    widgetContainer.className = 'tradingview-widget-container';
    widgetContainer.style.height = '100%';
    widgetContainer.style.width = '100%';

    const widgetDiv = document.createElement('div');
    widgetDiv.className = 'tradingview-widget-container__widget';
    widgetDiv.style.height = '100%';
    widgetDiv.style.width = '100%';
    widgetContainer.appendChild(widgetDiv);

    if (containerRef.current) {
      containerRef.current.appendChild(widgetContainer);
    }

    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-mini-symbol-overview.js';
    script.type = 'text/javascript';
    script.async = true;
    script.innerHTML = JSON.stringify({
      symbol: symbol,
      width: '100%',
      height: '100%',
      locale: 'en',
      dateRange: '1D',
      colorTheme: theme,
      isTransparent: true,
      autosize: true,
      largeChartUrl: '',
    });

    widgetContainer.appendChild(script);

    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [symbol, theme]);

  return (
    <div 
      ref={containerRef} 
      style={{ height: `${height}px`, width: '100%' }}
    />
  );
}

// Ticker Tape Widget
export function TradingViewTickerTape({ theme = 'dark' }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }

    const widgetContainer = document.createElement('div');
    widgetContainer.className = 'tradingview-widget-container';

    const widgetDiv = document.createElement('div');
    widgetDiv.className = 'tradingview-widget-container__widget';
    widgetContainer.appendChild(widgetDiv);

    if (containerRef.current) {
      containerRef.current.appendChild(widgetContainer);
    }

    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-ticker-tape.js';
    script.type = 'text/javascript';
    script.async = true;
    script.innerHTML = JSON.stringify({
      symbols: [
        { proName: 'BINANCE:BTCUSDT', title: 'Bitcoin' },
        { proName: 'BINANCE:ETHUSDT', title: 'Ethereum' },
        { proName: 'BINANCE:SOLUSDT', title: 'Solana' },
        { proName: 'NASDAQ:AAPL', title: 'Apple' },
        { proName: 'NASDAQ:TSLA', title: 'Tesla' },
        { proName: 'NASDAQ:NVDA', title: 'NVIDIA' },
        { proName: 'FX:EURUSD', title: 'EUR/USD' },
        { proName: 'COMEX:GC1!', title: 'Gold' },
      ],
      showSymbolLogo: true,
      isTransparent: true,
      displayMode: 'adaptive',
      colorTheme: theme,
      locale: 'en',
    });

    widgetContainer.appendChild(script);

    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [theme]);

  return <div ref={containerRef} />;
}

// Technical Analysis Widget
export function TradingViewTechnicalAnalysis({ symbol = 'BINANCE:BTCUSDT', theme = 'dark', height = 400 }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }

    const widgetContainer = document.createElement('div');
    widgetContainer.className = 'tradingview-widget-container';
    widgetContainer.style.height = '100%';
    widgetContainer.style.width = '100%';

    const widgetDiv = document.createElement('div');
    widgetDiv.className = 'tradingview-widget-container__widget';
    widgetDiv.style.height = '100%';
    widgetDiv.style.width = '100%';
    widgetContainer.appendChild(widgetDiv);

    if (containerRef.current) {
      containerRef.current.appendChild(widgetContainer);
    }

    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-technical-analysis.js';
    script.type = 'text/javascript';
    script.async = true;
    script.innerHTML = JSON.stringify({
      interval: '1h',
      width: '100%',
      isTransparent: true,
      height: '100%',
      symbol: symbol,
      showIntervalTabs: true,
      displayMode: 'single',
      locale: 'en',
      colorTheme: theme,
    });

    widgetContainer.appendChild(script);

    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [symbol, theme]);

  return (
    <div 
      ref={containerRef} 
      style={{ height: `${height}px`, width: '100%' }}
    />
  );
}

// Symbol Info Widget
export function TradingViewSymbolInfo({ symbol = 'BINANCE:BTCUSDT', theme = 'dark' }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }

    const widgetContainer = document.createElement('div');
    widgetContainer.className = 'tradingview-widget-container';

    const widgetDiv = document.createElement('div');
    widgetDiv.className = 'tradingview-widget-container__widget';
    widgetContainer.appendChild(widgetDiv);

    if (containerRef.current) {
      containerRef.current.appendChild(widgetContainer);
    }

    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-symbol-info.js';
    script.type = 'text/javascript';
    script.async = true;
    script.innerHTML = JSON.stringify({
      symbol: symbol,
      width: '100%',
      locale: 'en',
      colorTheme: theme,
      isTransparent: true,
    });

    widgetContainer.appendChild(script);

    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [symbol, theme]);

  return <div ref={containerRef} />;
}

export default memo(TradingViewWidget);
